import { theme } from 'antd';
import { DeviceGroupsIcon, InfoIcon, SearchIcon } from 'assets/images/svg';
import { SitesIcon } from 'assets/images/svg/Menu';
import { constants } from 'config/constants';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectCategories } from 'reduxStore/categories/selectors';
import { selectDefaultCategories } from 'reduxStore/defaultCategories/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectFloors } from 'reduxStore/floors/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { PopoverTag, TableFilterDropdown } from 'zComponents/atoms';
import { DeleteDeviceModal, DeviceDrawer, DeviceRemoteUnlockModal, GatewayDrawer } from 'zComponents/molecules';
import { Button, Col, Empty, Flex, Input, Row, Table, Tag, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;

const DeviceTab = ({ selectedSite, floorID }) => {
	// ✅ Hooks
	const {
		token: {
			fontSizeSM,
			colorTextDescription,
			fontWeightStrong,
			colorErrorText,
			colorWarningText
		}
	} = theme.useToken();

	// ✅ Redux state
	const allDevices = useSelector(selectDevices);
	const categories = useSelector(selectCategories);
	const defaultCategories = useSelector(selectDefaultCategories);
	const assetGroups = useSelector(selectAssetGroups);
	const floors = useSelector(selectFloors);
	const sites = useSelector(selectSites);

	// 1 Gather floorIDs for the current site
	const siteFloorIDs = useMemo(
		() => floors.filter((f) => f?.siteID === selectedSite)?.map((f) => f?.floorID),
		[floors, selectedSite]
	);

	// 2 Filter devices that live on any of those floors
	const siteDevices = useMemo(
		() => allDevices.filter((device) => device?.location?.some((loc) => siteFloorIDs?.includes(loc.floorID))),
		[allDevices, siteFloorIDs]
	);

	// 3 If a specific floor is selected, narrow to just that floor’s devices
	const devices = useMemo(
		() =>
			floorID
				? siteDevices.filter((device) => device?.location?.some((loc) => loc?.floorID === floorID))
				: siteDevices,
		[siteDevices, floorID]
	);

	// ✅ Local state
	const [selectedDeviceIDs, setSelectedDeviceIDs] = useState([]);
	const [selectedDevice, setSelectedDevice] = useState(null);
	const [openGatewayDrawer, setOpenGatewayDrawer] = useState(false);
	const [openDeviceDrawer, setOpenDeviceDrawer] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [inputValue, setInputValue] = useState('');

	// ✅ Debounced search handler
	const debouncedSearch = _.debounce((searchString) => {
		setSearchText(searchString);
	}, 300);

	// Apply filtering: only search by text
	const filteredDevices = useMemo(() => {
		return devices.filter((device) => {
			const matchesSearch =
				!searchText.length ||
				device.name?.toLowerCase().includes(searchText.toLowerCase()) ||
				device.description?.toLowerCase().includes(searchText.toLowerCase());
			return matchesSearch;
		});
	}, [devices, searchText]);

	// Create filter options
	const deviceOptions = useMemo(() => {
		const uniqueDeviceIDs = [...new Set(devices.map((device) => device.deviceID).filter(Boolean))];
		return uniqueDeviceIDs
			.map((deviceID) => {
				const device = devices.find((d) => d.deviceID === deviceID);
				return device ? { label: device.name || '', value: deviceID } : null;
			})
			.filter(Boolean)
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [devices]);

	// category options
	const categoryOptions = useMemo(() => {
		const uniqueCategoryIDs = [...new Set(devices.map((d) => d.categoryID).filter(Boolean))];
		return uniqueCategoryIDs
			.map((id) => {
				const category = categories.find((c) => c.categoryID === id);
				return category ? { label: category.name, value: id } : null;
			})
			.filter(Boolean)
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [devices, categories]);

	// floor options
	const floorOptions = useMemo(() => {
		const uniqueFloorIDs = [...new Set(devices.map((d) => d.location?.[0]?.floorID).filter(Boolean))];
		return uniqueFloorIDs
			.map((id) => {
				const floor = floors.find((f) => f.floorID === id);
				return floor ? { label: floor.name, value: id } : null;
			})
			.filter(Boolean)
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [devices, floors]);

	// device groups options
	const deviceGroupOptions = useMemo(() => {
		const allGroupNames = new Set();
		devices.forEach((device) => {
			const deviceGroups = assetGroups.filter((group) => group.devices?.includes(device.deviceID));
			deviceGroups.forEach((group) => allGroupNames.add(group.name));
		});
		return Array.from(allGroupNames)
			.map((groupName) => ({ label: groupName, value: groupName }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [devices, assetGroups]);

	// status options
	const statusOptions = useMemo(() => {
		const availableStatuses = new Set();
		devices.forEach((device) => {
			if (device.status === null || device.status === undefined) {
				availableStatuses.add('Unknown');
			} else if (device.status) {
				availableStatuses.add('Online');
			} else {
				availableStatuses.add('Offline');
			}
		});

		return Array.from(availableStatuses)
			.map((status) => ({ label: status, value: status }))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [devices]);

	// ✅ Handle input change
	const handleChangeInput = (e) => {
		const value = e.target.value;
		setInputValue(value);
		debouncedSearch(value);
	};

	// ✅ Row selection
	const rowSelection = {
		selectedRowKeys: selectedDeviceIDs,
		onChange: setSelectedDeviceIDs
	};

	// ✅ Table columns
	const columns = [
		{
			title: 'DEVICE',
			dataIndex: 'name',
			key: 'name',
			width: 300,
			render: (text, record) => {
				const defaultCategory = defaultCategories.find((cat) => cat.categoryID === record.defaultCategoryID);
				return (
					<Flex vertical>
						<Text strong style={{ lineHeight: '22px' }}>
							{text}
						</Text>
						<Text style={{ fontSize: fontSizeSM, lineHeight: '20px' }}>
							{defaultCategory?.name || 'N/A'}
						</Text>
					</Flex>
				);
			},
			sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
			onFilter: (value, record) => record.deviceID === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={deviceOptions} placeholder='Search devices' />
			)
		},
		{
			title: 'CATEGORY',
			dataIndex: 'defaultCategoryID',
			key: 'defaultCategoryID',
			render: (_, record) => {
				const category = categories.find((cat) => cat.categoryID === record.categoryID);
				return (
					<Flex vertical>
						<Text style={{ lineHeight: '22px' }}>{category?.name || 'N/A'}</Text>
					</Flex>
				);
			},
			sorter: (a, b) => {
				const aCategory = categories.find((cat) => cat.categoryID === a.categoryID)?.name || '';
				const bCategory = categories.find((cat) => cat.categoryID === b.categoryID)?.name || '';
				return aCategory.localeCompare(bCategory);
			},
			onFilter: (value, record) => record.categoryID === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={categoryOptions} placeholder='Search categories' />
			)
		},
		{
			title: 'SITE / FLOOR',
			dataIndex: 'floor',
			key: 'floor',
			render: (_, record) => {
				const deviceSites = record.sites || [];
				if (!deviceSites.length) return '-';

				const locations = record.location || [];

				const items = deviceSites.map((s, idx) => {
					const site = sites.find((st) => st.siteID === s.siteID);
					const siteFloors = locations
						.filter((loc) => loc.siteID === s.siteID && loc.floorID)
						.map((loc) => floors.find((f) => f.floorID === loc.floorID))
						.filter(Boolean);
					return {
						key: site?.siteID || idx,
						siteName: site?.name || '-',
						floors: siteFloors
					};
				});

				return (
					<PopoverTag
						icon={SitesIcon}
						count={deviceSites.length}
						singular='site'
						plural='sites'
						items={items}
						renderItem={(item) => (
							<Flex vertical key={item.key} gap={2}>
								<Flex align='center' gap={6}>
									<SitesIcon color={colorTextDescription} />
									<Text strong style={{ fontSize: 12 }}>{item.siteName}</Text>
								</Flex>
								{item.floors.length > 0 && (
									<Text type='secondary' style={{ fontSize: 11, paddingLeft: 22 }}>
										{item.floors.map((f) => f.name).join(', ')}
									</Text>
								)}
							</Flex>
						)}
					/>
				);
			},
			sorter: (a, b) => {
				const aSite = sites.find((s) => s.siteID === a.sites?.[0]?.siteID)?.name || '';
				const bSite = sites.find((s) => s.siteID === b.sites?.[0]?.siteID)?.name || '';
				return aSite.localeCompare(bSite);
			},
			onFilter: (value, record) => {
				return (record.location || []).some((loc) => loc.floorID === value);
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={floorOptions} placeholder='Search floors' />
			)
		},

		{
			title: 'STATUS',
			dataIndex: 'status',
			key: 'status',
			render: (status) => {
				if (status === null || status === undefined) {
					return (
						<Tag
							color='#F5F5F5'
							bordered={false}
							style={{ color: '#8C8C8C', fontWeight: fontWeightStrong, fontSizeSM }}
						>
							Unknown
						</Tag>
					);
				}

				const statusText = status ? 'Online' : 'Offline';
				const color = status ? '#D1FFDC' : '#FFD7D3';
				const textColor = status ? '#059641' : '#FF5949';
				return (
					<Tag
						color={color}
						bordered={false}
						style={{ color: textColor, fontWeight: fontWeightStrong, fontSizeSM }}
					>
						{statusText}
					</Tag>
				);
			},
			sorter: (a, b) => {
				// Handle null/undefined values - put them at the end
				if ((a.status === null || a.status === undefined) && (b.status === null || b.status === undefined)) {
					return 0;
				}
				if (a.status === null || a.status === undefined) {
					return 1;
				}
				if (b.status === null || b.status === undefined) {
					return -1;
				}
				// Sort by boolean value directly - true (Online) comes before false (Offline)
				return b.status - a.status;
			},
			onFilter: (value, record) => {
				if (record.status === null || record.status === undefined) {
					return value === 'Unknown';
				}
				const status = record.status ? 'Online' : 'Offline';
				return status === value;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={statusOptions} placeholder='Search status' />
			)
		},

		{
			title: 'BATTERY',
			dataIndex: 'battery',
			key: 'battery',
			render: (battery) => {
				let TextColor = '';

				if (battery === undefined || battery === null) {
					return <Text>-</Text>;
				}

				if (battery > 50) {
					TextColor = '#059641';
				} else if (battery > 25) {
					TextColor = colorWarningText;
				} else {
					TextColor = colorErrorText;
				}

				return (
					<Text strong style={{ color: TextColor }}>
						{battery}%
					</Text>
				);
			},
			sorter: (a, b) => {
				// Handle null/undefined values - put them at the end
				if (
					(a.battery === null || a.battery === undefined) &&
					(b.battery === null || b.battery === undefined)
				) {
					return 0;
				}
				if (a.battery === null || a.battery === undefined) {
					return 1;
				}
				if (b.battery === null || b.battery === undefined) {
					return -1;
				}
				// Sort by battery level (descending - highest first)
				return b.battery - a.battery;
			},
			disableFilter: true
		},
		{
			title: 'DEVICE GROUPS',
			dataIndex: 'devicesGroup',
			key: 'devicesGroup',
			width: 200,
			render: (_, record) => {
				const groups = assetGroups.filter((group) => group.devices?.includes(record.deviceID));
				const count = groups.length;

				if (count === 0)
					return (
						<Text type='secondary' style={{ fontSize: 12 }}>
							—
						</Text>
					);

				return (
					<PopoverTag
						icon={DeviceGroupsIcon}
						count={count}
						singular='group'
						plural='groups'
						items={groups.map((group) => ({ key: group.groupID, label: group.name }))}
					/>
				);
			},
			sorter: (a, b) => {
				const aGroups = assetGroups.filter((group) => group.devices?.includes(a.deviceID));
				const bGroups = assetGroups.filter((group) => group.devices?.includes(b.deviceID));

				const lengthDiff = aGroups.length - bGroups.length;
				if (lengthDiff !== 0) return lengthDiff;

				const aFirstGroup = aGroups[0]?.name || '';
				const bFirstGroup = bGroups[0]?.name || '';
				return aFirstGroup.localeCompare(bFirstGroup, undefined, { sensitivity: 'base' });
			},
			onFilter: (value, record) => {
				const recordGroups = assetGroups.filter((group) => group.devices?.includes(record.deviceID));
				return recordGroups.some((group) => group.name === value);
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={deviceGroupOptions} placeholder='Search device groups' />
			)
		},
		{
			title: 'ACTIONS',
			dataIndex: 'actions',
			align: 'center',
			width: 150,
			fixed: 'right',

			render: (_, record) => (
				<Flex gap={8} justify='center' onClick={(e) => e.stopPropagation()}>
					<Tooltip title='View Device Details' placement='top'>
						<Button
							type='text'
							icon={<InfoIcon color='currentColor' />}
							size='small'
							onClick={() => {
								setSelectedDevice(record.deviceID);
								setOpenGatewayDrawer(true);
							}}
						/>
					</Tooltip>
					<DeviceRemoteUnlockModal deviceID={record.deviceID} />
					<DeleteDeviceModal deviceID={record.deviceID} />
				</Flex>
			)
		}
	];

	return (
		<Row>
			<Col span={24} style={{ width: '100%', marginBottom: 24 }}>
				<Row justify='space-between'>
					<Col span={10}>
						<Input
							placeholder='Search Devices'
							value={inputValue}
							allowClear
							onChange={(e) => handleChangeInput(e)}
							onClear={() => setInputValue('')}
							suffix={<SearchIcon color={colorTextDescription} />}
						/>
					</Col>
				</Row>
			</Col>

			<Col span={24}>
				<Table
					dataSource={filteredDevices.map((device) => ({ ...device, key: device.deviceID }))}
					onRow={(record) => ({
						onClick: () => {
							setSelectedDevice(record.deviceID);
							if (constants.GATEWAY_CATEGORIES.includes(record.defaultCategoryID)) {
								setOpenGatewayDrawer(true);
							} else {
								setOpenDeviceDrawer(true);
							}
						},
						style: { cursor: 'pointer' }
					})}
					rowSelection={rowSelection}
					columns={columns}
					pagination={{
						defaultPageSize: 10,
						showQuickJumper: true,
						showSizeChanger: false
					}}
					size='small'
					locale={{
						emptyText: (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No devices found in this site.' />
						)
					}}
					scroll={{ x: 1500 }}
				/>
			</Col>

			<DeviceDrawer deviceID={selectedDevice} visible={openDeviceDrawer} setVisible={setOpenDeviceDrawer} />
			<GatewayDrawer deviceID={selectedDevice} visible={openGatewayDrawer} setVisible={setOpenGatewayDrawer} />
		</Row>
	);
};

export default DeviceTab;
